import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import type { BookingStatus, CustomerBooking } from '../api/types.ts';
import { useShop } from '../app/boot.tsx';
import { CustomerTabs, useShopPaths } from '../app/nav.tsx';
import { message } from '../lib/errors.ts';
import { minutesUntil, thaiDateTime } from '../lib/time.ts';
import { Banner, Button, Card, Chip, Dialog, Empty, Field, Loading, Page, useToast } from '../ui/index.tsx';
import { Book } from './Book.tsx';

const STATUS: Record<BookingStatus, { label: string; tone: 'ok' | 'accent' | 'muted' | 'dark' }> = {
  confirmed: { label: 'ยืนยันแล้ว', tone: 'dark' },
  completed: { label: 'เสร็จแล้ว', tone: 'ok' },
  no_show: { label: 'ไม่มา', tone: 'accent' },
  cancelled_customer: { label: 'ยกเลิกแล้ว', tone: 'muted' },
  cancelled_shop: { label: 'ร้านยกเลิก', tone: 'accent' },
  rescheduled: { label: 'เลื่อนแล้ว', tone: 'muted' },
};

type Data = { upcoming: CustomerBooking[]; past: CustomerBooking[] };

export function MyBookings() {
  const { api, shop, me } = useShop();
  const { go } = useShopPaths();
  const toast = useToast();
  const [data, setData] = useState<Data | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<CustomerBooking | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setData(await api.myBookings());
    } catch (e) {
      setLoadError(message(e));
    }
  }, [api]);

  useEffect(() => {
    if (!me.customer?.registered) { go(`/register?next=${encodeURIComponent('/my')}`, { replace: true }); return; }
    void load();
  }, [load, me.customer?.registered, go]);

  const cutoff = shop.rules.cancelCutoffMinutes;
  const cutoffHours = Math.round((cutoff / 60) * 10) / 10;
  const call = shop.phone ? { label: '📞 โทรหาร้าน', href: `tel:${shop.phone}` } : undefined;

  async function confirmCancel() {
    if (!cancelling) return;
    setBusy(true);
    try {
      await api.cancelBooking(cancelling.id, reason.trim() || undefined);
      toast('ยกเลิกการจองแล้ว', 'success');
      setCancelling(null);
      setReason('');
      await load();
    } catch (e) {
      toast(message(e), 'error');
      setCancelling(null);
      await load();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Page title="การจองของฉัน">
        {loadError && <Banner kind="error" action={{ label: 'ลองใหม่', onClick: () => void load() }}>{loadError}</Banner>}
        {!data && !loadError && <Loading />}
        {data && (
          <>
            {data.upcoming.length === 0
              ? <Empty title="ยังไม่มีการจองล่วงหน้า" action={{ label: 'จองคิว', onClick: () => go('/book') }} />
              : (
                <div className="list">
                  {data.upcoming.map((b) => {
                    const locked = minutesUntil(b.startAt) < cutoff;
                    return (
                      <Card key={b.id}>
                        <div className="row-between">
                          <div className="card-title">{b.serviceName}</div>
                          <Chip tone="dark">ยืนยันแล้ว</Chip>
                        </div>
                        <p style={{ marginTop: 4 }}>{thaiDateTime(b.startAt)}</p>
                        <p className="muted small">ช่าง{b.barberName}</p>
                        {b.note && <p className="muted small">หมายเหตุ: {b.note}</p>}
                        {locked && (
                          <div style={{ marginTop: 10 }}>
                            <Banner kind="warn" action={call}>เหลือน้อยกว่า {cutoffHours} ชม. กรุณาโทรหาร้าน</Banner>
                          </div>
                        )}
                        <div className="row" style={{ marginTop: 10 }}>
                          <Button variant="secondary" className="btn-sm" disabled={locked} onClick={() => go(`/my/${b.id}/reschedule`)}>เลื่อนนัด</Button>
                          <Button variant="ghost" className="btn-sm" disabled={locked} onClick={() => setCancelling(b)}>ยกเลิก</Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            {data.past.length > 0 && (
              <>
                <p className="section-title">ประวัติ</p>
                <div className="list">
                  {data.past.map((b) => (
                    <Card key={b.id}>
                      <div className="row-between">
                        <div className="card-title">{b.serviceName}</div>
                        <Chip tone={STATUS[b.status].tone}>{STATUS[b.status].label}</Chip>
                      </div>
                      <p className="muted small">{thaiDateTime(b.startAt)} · ช่าง{b.barberName}</p>
                      {b.status === 'cancelled_shop' && b.cancelReason && <p className="muted small">เหตุผล: {b.cancelReason}</p>}
                    </Card>
                  ))}
                </div>
              </>
            )}
          </>
        )}
        <Dialog open={cancelling !== null} title="ยกเลิกการจองนี้?" confirmLabel="ยืนยันยกเลิก" cancelLabel="ไม่ยกเลิก" danger loading={busy}
          onConfirm={confirmCancel} onCancel={() => { setCancelling(null); setReason(''); }}>
          {cancelling && <p style={{ marginBottom: 12 }}>{cancelling.serviceName} · {thaiDateTime(cancelling.startAt)}</p>}
          <Field label="เหตุผล (ถ้ามี)">
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} />
          </Field>
        </Dialog>
      </Page>
      <CustomerTabs />
    </>
  );
}

/** /my/:id/reschedule — finds the booking, then reuses the booking wizard. */
export function Reschedule() {
  const { id = '' } = useParams();
  const { api } = useShop();
  const { go } = useShopPaths();
  const [target, setTarget] = useState<CustomerBooking | null | undefined>(undefined);
  useEffect(() => {
    api.myBookings().then((d) => setTarget(d.upcoming.find((b) => b.id === id) ?? null)).catch(() => setTarget(null));
  }, [api, id]);
  if (target === undefined) return <Loading />;
  if (target === null) return <Page title="เลื่อนนัด"><Empty title="ไม่พบการจองนี้" action={{ label: 'การจองของฉัน', onClick: () => go('/my') }} /></Page>;
  return <Book reschedule={{ id: target.id, serviceId: target.serviceId }} />;
}
